// "Today's to-dos" daily digest card inside the notification bell. Items stay in sync with CRM activities + Workspace tasks.
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, Check, Clock, ExternalLink, CalendarClock, ListChecks, FileText, Store, Activity, X } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

const KIND_ICON = { activity: Activity, task: ListChecks, quote: FileText, trade: Store };
const LATER = [["1h", "1 hour"], ["3h", "3 hours"], ["tomorrow", "Tomorrow 9am"]];
const ymd = (days) => { const d = new Date(); d.setDate(d.getDate() + days); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; };
const MOVE = [["Today", 0], ["Tomorrow", 1], ["+3 days", 3], ["+1 week", 7]];
const chip = "px-2 py-1 rounded-md border border-white/10 text-[11px] text-slate-200 hover:border-amber-400/50 hover:text-white transition-colors";
const iconBtn = "p-1.5 rounded-md border border-white/10 bg-white/[0.03]";

function WhenRow({ it, i, busy, onAct, onClose }) {
  const canMove = it.kind === "activity" || it.kind === "task";
  return (
    <div className="flex flex-wrap items-center gap-1.5 px-2 pb-2 pt-1" data-testid={`notif-digest-when-${i}`}>
      {canMove && <>
        <span className="text-[10px] font-mono uppercase text-slate-500 mr-0.5">Move due to</span>
        {MOVE.map(([l, n]) => <button key={l} type="button" disabled={busy} className={chip} onClick={() => onAct(it, "reschedule", ymd(n))} data-testid={`notif-digest-move-${i}-${n}`}>{l}</button>)}
        <input type="date" disabled={busy} onChange={(e) => e.target.value && onAct(it, "reschedule", e.target.value)} className="h-7 px-1.5 rounded-md bg-slate-900/70 border border-white/10 text-[11px] text-slate-200" data-testid={`notif-digest-move-date-${i}`} />
        <span className="w-px h-4 bg-white/10 mx-1" />
      </>}
      <span className="text-[10px] font-mono uppercase text-slate-500 mr-0.5">Remind me</span>
      {LATER.map(([k, l]) => <button key={k} type="button" disabled={busy} className={chip} onClick={() => onAct(it, "snooze", k)} data-testid={`notif-digest-later-${i}-${k}`}>{l}</button>)}
      <button type="button" onClick={onClose} className="ml-auto p-1 text-slate-500 hover:text-white" aria-label="Close"><X className="w-3.5 h-3.5" /></button>
    </div>
  );
}

export default function DigestCard({ n, onChanged, onClose }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [when, setWhen] = useState(null);
  const [all, setAll] = useState(false);
  const today = ymd(0);

  const itemAct = async (it, action, choice) => {
    setBusy(true);
    try {
      await api.post(`/notifications/${n._id}/item`, { key: it.key, action, choice });
      toast.success(action === "done" ? "Marked done (CRM + Workspace)" : action === "reschedule" ? `Due date moved to ${choice}` : "We'll remind you later");
      setWhen(null); onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update"); }
    finally { setBusy(false); }
  };
  const openIt = (it) => {
    onClose();
    if (it.kind === "quote") navigate(`/quotes/${it.id}/edit`);
    else if (it.person_id) navigate(`/crm/persons/${it.person_id}`);
    else if (it.kind === "task") navigate("/workspace/my-tasks");
    else navigate("/my-dashboard");
  };
  const ack = async () => { await api.post(`/notifications/${n._id}/acknowledge`); toast.success("Acknowledged — nice work"); onChanged(); };
  const snoozeAll = async (choice) => { await api.post(`/notifications/${n._id}/snooze`, { choice }); toast.success("We'll remind you later"); onChanged(); };

  const items = n.items || [];
  return (
    <div className={`border-b border-white/10 ${n.acknowledged ? "" : "bg-rose-500/[0.05]"}`} data-testid="notif-digest">
      <div className="flex items-center gap-2.5 px-4 pt-3.5 pb-2">
        <span className="w-7 h-7 grid place-items-center rounded-md bg-rose-500/15 text-rose-300 shrink-0"><CalendarClock className="w-4 h-4" /></span>
        <div className="min-w-0 flex-1">
          <div className="text-[13.5px] font-semibold text-white">{n.title}</div>
          <div className="text-[11.5px] text-slate-400" data-testid="notif-digest-summary">{n.body}</div>
        </div>
        {n.acknowledged && <span className="text-[9.5px] font-mono uppercase text-emerald-300">acknowledged</span>}
      </div>
      <div className="px-3 space-y-1.5" data-testid="notif-digest-items">
        {items.length === 0 && <div className="text-[12px] text-emerald-300 px-1 py-2">All clear for today.</div>}
        {(all ? items : items.slice(0, 8)).map((it, i) => {
          const Icon = KIND_ICON[it.kind] || Activity;
          const label = it.overdue ? `${Math.max(1, Math.round((new Date(today) - new Date(it.due)) / 86400000))}d over` : it.due === today ? "today" : it.due;
          return (
            <div key={it.key} className={`rounded-lg border ${it.overdue ? "border-rose-500/30 bg-rose-500/[0.05]" : "border-white/[0.06] bg-white/[0.02]"}`} data-testid={`notif-digest-item-${i}`}>
              <div className="flex items-center gap-2.5 px-2.5 py-2">
                {it.overdue ? <AlertTriangle className="w-4 h-4 text-rose-400 t2-bang shrink-0" /> : <Icon className="w-4 h-4 text-amber-300 shrink-0" />}
                <div className="min-w-0 flex-1">
                  <div className="text-[12.5px] text-slate-100 truncate" title={it.title}>{it.title}</div>
                  <div className="text-[10.5px] text-slate-500 font-mono">{it.sub} · <span className={it.overdue ? "text-rose-300" : "text-amber-300"}>{label}</span></div>
                </div>
                {(it.kind === "activity" || it.kind === "task") && (
                  <button type="button" disabled={busy} onClick={() => itemAct(it, "done")} title="Mark done" data-testid={`notif-digest-done-${i}`}
                    className={`${iconBtn} text-emerald-300 hover:border-emerald-400/50`}><Check className="w-3.5 h-3.5" /></button>
                )}
                <button type="button" onClick={() => setWhen(when === it.key ? null : it.key)} title="Move due date / remind me later" data-testid={`notif-digest-later-${i}`}
                  className={`${iconBtn} ${when === it.key ? "border-amber-400/60 text-amber-200" : "text-amber-300 hover:border-amber-400/50"}`}><Clock className="w-3.5 h-3.5" /></button>
                <button type="button" onClick={() => openIt(it)} title="Open" data-testid={`notif-digest-open-${i}`}
                  className={`${iconBtn} text-sky-300 hover:border-sky-400/50`}><ExternalLink className="w-3.5 h-3.5" /></button>
              </div>
              {when === it.key && <WhenRow it={it} i={i} busy={busy} onAct={itemAct} onClose={() => setWhen(null)} />}
            </div>
          );
        })}
        {items.length > 8 && (
          <button type="button" onClick={() => setAll((v) => !v)} className="w-full py-1.5 text-[11.5px] text-teal-300 hover:underline" data-testid="notif-digest-more">
            {all ? "Show fewer" : `Show ${items.length - 8} more`}</button>
        )}
      </div>
      {!n.acknowledged && (
        <div className="flex flex-wrap items-center gap-2 px-4 py-3">
          <button type="button" onClick={ack} data-testid="notif-digest-ack"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12px] font-semibold bg-emerald-500/15 border border-emerald-500/40 text-emerald-200 hover:bg-emerald-500/25">
            <Check className="w-3.5 h-3.5" /> Acknowledge &amp; clear
          </button>
          <span className="ml-auto inline-flex items-center gap-1.5 text-[11px] text-slate-400">Remind me
            {LATER.map(([k, l]) => (
              <button key={k} type="button" onClick={() => snoozeAll(k)} data-testid={`notif-digest-snooze-${k}`} className={chip}>{l}</button>
            ))}
          </span>
        </div>
      )}
    </div>
  );
}
